/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

import "./Modal.css";

import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export function useModal(): [
    JSX.Element | null,
    (title: string, showModal: (onClose: () => void) => JSX.Element) => void,
] {
    const [modalContent, setModalContent] = useState<null | {
        closeOnClickOutside: boolean;
        content: JSX.Element;
        title: string;
    }>(null);

    const onClose = useCallback(() => {
        setModalContent(null);
    }, []);

    const modal = useMemo(() => {
        if (modalContent === null) {
            return null;
        }
        const { title, content, closeOnClickOutside } = modalContent;
        return (
            <Modal onClose={onClose} title={title} closeOnClickOutside={closeOnClickOutside}>
                {content}
            </Modal>
        );
    }, [modalContent, onClose]);

    const showModal = useCallback(
        (
            title: string,
            // eslint-disable-next-line no-shadow
            getContent: (onClose: () => void) => JSX.Element,
            closeOnClickOutside = false,
        ) => {
            setModalContent({
                closeOnClickOutside,
                content: getContent(onClose),
                title,
            });
        },
        [onClose],
    );

    return [modal, showModal];
}

/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */

function PortalImpl({
    onClose,
    children,
    title,
    closeOnClickOutside,
}: {
    children: ReactNode;
    closeOnClickOutside: boolean;
    onClose: () => void;
    title: string;
}) {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (modalRef.current !== null) {
            modalRef.current.focus();
        }
    }, []);

    useEffect(() => {
        let modalOverlayElement: HTMLElement | null = null;
        const handler = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        const clickOutsideHandler = (event: MouseEvent) => {
            const target = event.target;
            if (modalRef.current !== null && !modalRef.current.contains(target as Node) && closeOnClickOutside) {
                onClose();
            }
        };
        const modelElement = modalRef.current;
        if (modelElement !== null) {
            modalOverlayElement = modelElement.parentElement;
            if (modalOverlayElement !== null) {
                modalOverlayElement.addEventListener("click", clickOutsideHandler);
            }
        }

        window.addEventListener("keydown", handler);

        return () => {
            window.removeEventListener("keydown", handler);
            if (modalOverlayElement !== null) {
                modalOverlayElement?.removeEventListener("click", clickOutsideHandler);
            }
        };
    }, [closeOnClickOutside, onClose]);

    return (
        <div className="Modal__overlay" role="dialog">
            <div className="Modal__modal" tabIndex={-1} ref={modalRef}>
                <h2 className="Modal__title">{title}</h2>
                <button className="Modal__closeButton" aria-label="Close modal" type="button" onClick={onClose}>
                    X
                </button>
                <div className="Modal__content">{children}</div>
            </div>
        </div>
    );
}

export function Modal({
    onClose,
    children,
    title,
    closeOnClickOutside = false,
}: {
    children: ReactNode;
    closeOnClickOutside?: boolean;
    onClose: () => void;
    title: string;
}): JSX.Element {
    return createPortal(
        <PortalImpl onClose={onClose} title={title} closeOnClickOutside={closeOnClickOutside}>
            {children}
        </PortalImpl>,
        document.body,
    );
}
